export interface UploadedFile {
  size: number;
  name: string;
  type: string;
  lastModified: number;
  status: "uploaded";
}

export type FileOrUploaded = File | UploadedFile;

export interface ProcessedFile<T = unknown> {
  id: string;
  file:
    | FileOrUploaded
    | { size: number; name: string; type: string; lastModified: number };
  progress?: number;
  error?: string;
  data?: T;
  missing?: boolean;
  status?: "uploaded";
}

export interface ImageMetadata {
  exif?: {
    geoLocation?: { longitude: number; latitude: number; altitude?: number };
    dateTime?: string;
    make?: string;
    model?: string;
  };
  preview?: string;
}

export interface PointCloudData {
  positions: Float32Array;
  colors?: Uint8Array;
  bbox: number[];
  timestamps?: [number, number];
  points: number;
  origin: [number, number, number];
}

export interface GeometryData {
  collection?: GeoJSON.FeatureCollection;
  bbox?: number[];
}

export type GeometryFileType =
  | "shp"
  | "shx"
  | "dbf"
  | "prj"
  | "kml"
  | "kmz"
  | "geojson"
  | "csv"
  | "pdf"
  | "zip"
  | "xlsx";

export type ProcessedImage = ProcessedFile<ImageMetadata>;

export type ShapefileGeometry = ProcessedFile<GeometryData> & {
  name: string;
  label?: string;
  type: "shp";
  componentKeys?: {
    shp?: string;
    shx?: string;
    dbf?: string;
    prj?: string;
  };
  files: {
    shp?: FileOrUploaded;
    shx?: FileOrUploaded;
    dbf?: FileOrUploaded;
    prj?: FileOrUploaded;
  };
};

export type GeoJSONGeometry = ProcessedFile<GeometryData> & {
  name: string;
  label?: string;
  type: "geojson";
  files: {
    geojson?: FileOrUploaded;
  };
};

export type KMLGeometry = ProcessedFile<GeometryData> & {
  name: string;
  label?: string;
  type: "kml" | "kmz";
  files: {
    kml?: FileOrUploaded;
    kmz?: FileOrUploaded;
  };
};

export type CSVGeometry = ProcessedFile<GeometryData> & {
  name: string;
  label?: string;
  type: "csv";
  files: {
    csv?: FileOrUploaded;
  };
};

export type PDFGeometry = ProcessedFile<GeometryData> & {
  name: string;
  label?: string;
  type: "pdf";
  files: {
    pdf?: FileOrUploaded;
  };
};

export type XLSXGeometry = ProcessedFile<GeometryData> & {
  name: string;
  label?: string;
  type: "xlsx";
  files: {
    xlsx?: FileOrUploaded;
  };
};

export type ProcessedGeometry =
  | ShapefileGeometry
  | GeoJSONGeometry
  | KMLGeometry
  | CSVGeometry
  | PDFGeometry
  | XLSXGeometry;

export type ProcessedPointCloud = ProcessedFile<PointCloudData>;

export interface MachineContext<T extends ProcessedFile> {
  uploads: T[];
  active: string | string[] | null;
  removing: string | null;
  surveyId: number;
  orderId: number;
}

export type FileProcessingEvent =
  | { type: "MAP_LOADED" }
  | { type: "ADD_FILES"; files: File[] }
  | { type: "REMOVE_FILE"; id: string }
  | { type: "CONFIRM_REMOVE" }
  | { type: "CANCEL_REMOVE" }
  | { type: "REMOVE_ALL" }
  | { type: "CONFIRM_REMOVE_ALL" }
  | { type: "CANCEL_REMOVE_ALL" }
  | {
      type: "UPDATE_LABEL";
      id: string;
      label?: string;
    }
  | { type: "RESET" }
  | { type: "PROCESSING_START"; id: string }
  | { type: "PROCESSING_PROGRESS"; id: string; progress: number }
  | { type: "PROCESSING_COMPLETE"; id: string; data: any }
  | { type: "PROCESSING_ERROR"; id: string; error: string }
  | { type: "MARKER_OVER"; id: string }
  | { type: "MARKER_OUT"; id: string }
  | { type: "MARKER_SELECT"; id: string }
  | { type: "MARKER_DESELECT"; id: string }
  | { type: "MARKER_DESELECT_ALL" }
  | { type: "MARKER_RESET" }
  | { type: "REMOVE_SELECTED" }
  | { type: "CONFIRM_REMOVE_SELECTED" }
  | { type: "REMOVE_MISSING" }
  | { type: "CONFIRM_REMOVE_MISSING" };

export enum MachineState {
  IDLE = "idle",
  IDENTIFY = "identify",
  PROCESSING = "processing",
  COMPLETED = "completed",
}

export function isUploadedFile(file: any): file is UploadedFile {
  return file && typeof file === "object" && file.status === "uploaded";
}

export function isProcessedFileUploaded(upload: ProcessedFile): boolean {
  return upload.status === "uploaded";
}
